import * as THREE from "three";
import { TerrainSampler } from "./Noise";
import { TERRAIN, MAP_PARAMS, type MapType, type MapParams } from "../shared/protocol";
import { generateBuildings, type Building } from "../shared/Buildings";

const PAL = {
  sea:        new THREE.Color("#2a6f97"),
  seaDeep:    new THREE.Color("#0d3756"),
  beach:      new THREE.Color("#dec18b"),
  grass:      new THREE.Color("#4f8a3f"),
  grassDark:  new THREE.Color("#3d6f30"),
  rock:       new THREE.Color("#7d7c79"),
  snow:       new THREE.Color("#f4f4f8"),
  asphalt:    new THREE.Color("#3a3f48"),
  asphaltLt:  new THREE.Color("#52585f"),
  building:   new THREE.Color("#caa472"),
  buildingDk: new THREE.Color("#8c6f4b"),
  roof:       new THREE.Color("#a13c2f"),
  tower1:     new THREE.Color("#9aa3ad"),
  tower2:     new THREE.Color("#7a8088"),
  tower3:     new THREE.Color("#b4bbc4"),
  tower4:     new THREE.Color("#646970"),
  glass:      new THREE.Color("#5b8aa6"),
  tree:       new THREE.Color("#2f5e2c"),
  treeDk:     new THREE.Color("#1f4220"),
};

function colorForHeight(h: number, sea: number, max: number, urban: boolean): THREE.Color {
  if (h < sea + 0.4)  return urban ? PAL.asphalt.clone() : PAL.beach.clone();
  if (urban) {
    // Urban: gray asphalt with subtle variation
    const t = Math.min(1, (h - sea) / 8);
    return PAL.asphalt.clone().lerp(PAL.asphaltLt, t * 0.7);
  }
  if (h < sea + 4)    return PAL.beach.clone().lerp(PAL.grass, (h - sea - 0.4) / 3.6);
  if (h < max * 0.45) return PAL.grass.clone().lerp(PAL.grassDark, (h - 4 - sea) / Math.max(1, (max * 0.45 - 4 - sea)));
  if (h < max * 0.72) return PAL.grassDark.clone().lerp(PAL.rock, (h - max * 0.45) / (max * 0.27));
  return PAL.rock.clone().lerp(PAL.snow, Math.min(1, (h - max * 0.72) / (max * 0.28)));
}

export interface WorldHandles {
  group: THREE.Group;
  sampler: TerrainSampler;
  params: MapParams;
  mapType: MapType;
  buildings: Building[];
  heightAt: (x: number, z: number) => number;
}

// Apply per-map shape on top of the base sampler — must match Buildings.ts exactly.
function shapedHeight(sampler: TerrainSampler, x: number, z: number, params: MapParams): number {
  const raw = sampler.height(x, z, TERRAIN.maxHeight);
  let h = raw * params.heightAmp;
  if (params.flatten > 0) h = h * (1 - params.flatten) + params.seaLevel * params.flatten;
  return h;
}

export function buildWorld(seed: number, mapType: MapType): WorldHandles {
  const params = MAP_PARAMS[mapType];
  const sampler = new TerrainSampler(seed);
  const group = new THREE.Group();
  group.name = `World:${mapType}`;

  const size = TERRAIN.size;
  const seg = TERRAIN.segments;
  const seaLevel = params.seaLevel;
  const maxH = TERRAIN.maxHeight;
  const urban = mapType === "metropolis";

  // ---- Terrain ----
  const geom = new THREE.PlaneGeometry(size, size, seg, seg);
  geom.rotateX(-Math.PI / 2);
  const pos = geom.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const z = pos.getZ(i);
    let h = shapedHeight(sampler, x, z, params);
    if (h < seaLevel - 0.5) h = seaLevel - 0.5 + (h - (seaLevel - 0.5)) * 0.4;
    pos.setY(i, h);
  }
  const flat = geom.toNonIndexed();
  flat.computeVertexNormals();
  const fpos = flat.attributes.position as THREE.BufferAttribute;
  const fcolors = new Float32Array(fpos.count * 3);
  for (let f = 0; f < fpos.count; f += 3) {
    const ay = fpos.getY(f), by = fpos.getY(f + 1), cy = fpos.getY(f + 2);
    const ax = fpos.getX(f), az = fpos.getZ(f);
    const hAvg = (ay + by + cy) / 3;
    const col = colorForHeight(hAvg, seaLevel, maxH, urban);
    const jitter = 0.92 + ((ax + az) % 7) * 0.012;
    col.multiplyScalar(jitter);
    for (let k = 0; k < 3; k++) {
      fcolors[(f + k) * 3 + 0] = col.r;
      fcolors[(f + k) * 3 + 1] = col.g;
      fcolors[(f + k) * 3 + 2] = col.b;
    }
  }
  flat.setAttribute("color", new THREE.BufferAttribute(fcolors, 3));

  const terrainMat = new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true });
  const terrain = new THREE.Mesh(flat, terrainMat);
  group.add(terrain);

  // ---- Sea ----
  if (seaLevel > -10) {
    const seaGeo = new THREE.PlaneGeometry(size * 1.5, size * 1.5, 8, 8);
    seaGeo.rotateX(-Math.PI / 2);
    const seaMat = new THREE.MeshBasicMaterial({ color: PAL.sea, transparent: true, opacity: urban ? 0.0 : 0.85 });
    const sea = new THREE.Mesh(seaGeo, seaMat);
    sea.position.y = seaLevel;
    group.add(sea);
    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(size * 1.5, size * 1.5).rotateX(-Math.PI / 2),
      new THREE.MeshBasicMaterial({ color: PAL.seaDeep, transparent: urban, opacity: urban ? 0 : 1 }),
    );
    floor.position.y = seaLevel - 6;
    group.add(floor);
  }

  // ---- Buildings (shared deterministic generator) ----
  const buildings = generateBuildings(seed, sampler, params);
  if (urban) {
    addSkyscrapers(group, buildings);
  } else {
    addHouses(group, buildings);
  }

  // ---- Trees (non-urban only) ----
  if (params.treeDensity > 0 && !urban) {
    addTrees(group, sampler, seed, params);
  }

  // ---- Lights ----
  const sun = new THREE.DirectionalLight(0xffffff, urban ? 0.85 : 1.0);
  sun.position.set(300, 600, 200);
  group.add(sun);
  const hemi = new THREE.HemisphereLight(urban ? 0xb8c8d8 : 0xc8e0ff, urban ? 0x2a2e34 : 0x3b5530, urban ? 0.45 : 0.7);
  group.add(hemi);

  return {
    group, sampler, params, mapType, buildings,
    heightAt: (x, z) => shapedHeight(sampler, x, z, params),
  };
}

function addSkyscrapers(group: THREE.Group, buildings: Building[]) {
  // Use ONE unit cube geometry, scaled per instance — that's the cheap path
  // for thousands of buildings. Two color variants for visual variety.
  const cubeGeo = new THREE.BoxGeometry(1, 1, 1);
  cubeGeo.translate(0, 0.5, 0); // base at y=0
  const matA = new THREE.MeshLambertMaterial({ color: PAL.tower1, flatShading: true });
  const matB = new THREE.MeshLambertMaterial({ color: PAL.tower2, flatShading: true });
  const matC = new THREE.MeshLambertMaterial({ color: PAL.tower3, flatShading: true });
  const matD = new THREE.MeshLambertMaterial({ color: PAL.tower4, flatShading: true });
  const mats = [matA, matB, matC, matD];

  // Bucket by material color
  const buckets: Building[][] = [[], [], [], []];
  for (let i = 0; i < buildings.length; i++) {
    const b = buildings[i];
    if (b.kind !== 1) continue;
    buckets[i & 3].push(b);
  }

  const dummy = new THREE.Object3D();
  for (let i = 0; i < buckets.length; i++) {
    const bucket = buckets[i];
    if (!bucket.length) continue;
    const inst = new THREE.InstancedMesh(cubeGeo, mats[i], bucket.length);
    inst.frustumCulled = true;
    for (let j = 0; j < bucket.length; j++) {
      const b = bucket[j];
      dummy.position.set(b.x, b.yBase, b.z);
      dummy.scale.set(b.w, b.h, b.d);
      dummy.rotation.set(0, b.rot, 0);
      dummy.updateMatrix();
      inst.setMatrixAt(j, dummy.matrix);
    }
    inst.instanceMatrix.needsUpdate = true;
    group.add(inst);
  }

  // Roof caps (small flat box on top of each tower) — single instanced mesh.
  const capGeo = new THREE.BoxGeometry(1, 1, 1);
  capGeo.translate(0, 0.5, 0);
  const capMat = new THREE.MeshLambertMaterial({ color: 0x4a4f55, flatShading: true });
  const towers = buildings.filter(b => b.kind === 1);
  if (towers.length) {
    const inst = new THREE.InstancedMesh(capGeo, capMat, towers.length);
    for (let j = 0; j < towers.length; j++) {
      const b = towers[j];
      dummy.position.set(b.x, b.yBase + b.h, b.z);
      dummy.scale.set(b.w * 0.7, 2.0, b.d * 0.7);
      dummy.rotation.set(0, b.rot, 0);
      dummy.updateMatrix();
      inst.setMatrixAt(j, dummy.matrix);
    }
    inst.instanceMatrix.needsUpdate = true;
    group.add(inst);
  }
}

function addHouses(group: THREE.Group, buildings: Building[]) {
  const houseGeo = new THREE.BoxGeometry(1, 1, 1);
  houseGeo.translate(0, 0.5, 0);
  const roofGeo = new THREE.ConeGeometry(0.75, 0.5, 4);
  roofGeo.rotateY(Math.PI / 4);
  roofGeo.translate(0, 0.25, 0);
  const matH = new THREE.MeshLambertMaterial({ color: PAL.building, flatShading: true });
  const matH2 = new THREE.MeshLambertMaterial({ color: PAL.buildingDk, flatShading: true });
  const matR = new THREE.MeshLambertMaterial({ color: PAL.roof, flatShading: true });

  const houses = buildings.filter(b => b.kind === 0);
  if (!houses.length) return;
  const dummy = new THREE.Object3D();

  const instA = new THREE.InstancedMesh(houseGeo, matH, Math.ceil(houses.length / 2));
  const instB = new THREE.InstancedMesh(houseGeo, matH2, Math.floor(houses.length / 2));
  const instR = new THREE.InstancedMesh(roofGeo, matR, houses.length);
  let aI = 0, bI = 0;
  for (let j = 0; j < houses.length; j++) {
    const b = houses[j];
    dummy.position.set(b.x, b.yBase, b.z);
    dummy.scale.set(b.w, b.h, b.d);
    dummy.rotation.set(0, b.rot, 0);
    dummy.updateMatrix();
    if (j & 1) instB.setMatrixAt(bI++, dummy.matrix);
    else      instA.setMatrixAt(aI++, dummy.matrix);

    dummy.position.set(b.x, b.yBase + b.h, b.z);
    dummy.scale.set(b.w * 1.08, b.h * 0.4, b.d * 1.08);
    dummy.updateMatrix();
    instR.setMatrixAt(j, dummy.matrix);
  }
  instA.count = aI; instB.count = bI;
  instA.instanceMatrix.needsUpdate = true;
  instB.instanceMatrix.needsUpdate = true;
  instR.instanceMatrix.needsUpdate = true;
  group.add(instA); group.add(instB); group.add(instR);
}

function addTrees(group: THREE.Group, sampler: TerrainSampler, seed: number, params: MapParams) {
  const trunkGeo = new THREE.CylinderGeometry(0.4, 0.5, 3, 5);
  trunkGeo.translate(0, 1.5, 0);
  const topGeo = new THREE.ConeGeometry(2.4, 5.5, 6);
  topGeo.translate(0, 5.0, 0);
  const trunkMat = new THREE.MeshLambertMaterial({ color: 0x6b4a2b, flatShading: true });
  const treeMat = new THREE.MeshLambertMaterial({ color: PAL.tree, flatShading: true });
  const treeMat2 = new THREE.MeshLambertMaterial({ color: PAL.treeDk, flatShading: true });

  const step = 50;
  const half = TERRAIN.size / 2;
  const tops: THREE.Matrix4[] = [];
  const tops2: THREE.Matrix4[] = [];
  const trunks: THREE.Matrix4[] = [];

  const dummy = new THREE.Object3D();
  for (let x = -half; x < half; x += step) {
    for (let z = -half; z < half; z += step) {
      // Hash-based deterministic placement
      const h = Math.sin(x * 0.13 + z * 0.17) + Math.cos(x * 0.07 + z * 0.11);
      if (h <= 0) continue;
      if (Math.random() > params.treeDensity) continue;
      const ox = x + (Math.random() - 0.5) * step * 0.7;
      const oz = z + (Math.random() - 0.5) * step * 0.7;
      const ground = sampler.height(ox, oz, TERRAIN.maxHeight) * params.heightAmp;
      if (ground <= params.seaLevel + 0.5 || ground > TERRAIN.maxHeight * 0.5) continue;
      dummy.position.set(ox, ground, oz);
      dummy.rotation.y = Math.random() * Math.PI * 2;
      const s = 0.8 + Math.random() * 0.5;
      dummy.scale.setScalar(s);
      dummy.updateMatrix();
      trunks.push(dummy.matrix.clone());
      ((trunks.length & 1) ? tops2 : tops).push(dummy.matrix.clone());
    }
    if (seed) {} // referenced for clarity
  }
  if (trunks.length) {
    const inst = new THREE.InstancedMesh(trunkGeo, trunkMat, trunks.length);
    for (let i = 0; i < trunks.length; i++) inst.setMatrixAt(i, trunks[i]);
    inst.instanceMatrix.needsUpdate = true; group.add(inst);
  }
  if (tops.length) {
    const inst = new THREE.InstancedMesh(topGeo, treeMat, tops.length);
    for (let i = 0; i < tops.length; i++) inst.setMatrixAt(i, tops[i]);
    inst.instanceMatrix.needsUpdate = true; group.add(inst);
  }
  if (tops2.length) {
    const inst = new THREE.InstancedMesh(topGeo, treeMat2, tops2.length);
    for (let i = 0; i < tops2.length; i++) inst.setMatrixAt(i, tops2[i]);
    inst.instanceMatrix.needsUpdate = true; group.add(inst);
  }
}
